import { PointerEvent as ReactPointerEvent, useEffect, useRef, useState } from 'react';
import { clientToCssPixels, readViewportCssSize } from '../../../utils/displaySize';

export interface ModalSize {
  width: number;
  height: number;
}

interface ResizeGesture {
  pointerX: number;
  pointerY: number;
  startSize: ModalSize;
}

/** Room kept between a dialog and the pane edges, the same as antd's own calc(100vw - 32px) cap. */
export const PANE_MARGIN_PX = 16;

export const readPaneSize = (): ModalSize => {
  const viewport = readViewportCssSize();
  return {
    width: viewport.width - 2 * PANE_MARGIN_PX,
    height: viewport.height - 2 * PANE_MARGIN_PX,
  };
};

/** The pane wins over the minimum, so a dialog still fits a pane narrower than its default size. */
export const clampModalSize = (
  size: ModalSize,
  minSize: ModalSize,
  paneSize: ModalSize,
): ModalSize => ({
  width: Math.min(Math.max(size.width, minSize.width), paneSize.width),
  height: Math.min(Math.max(size.height, minSize.height), paneSize.height),
});

/**
 * Corner-handle resizing for a centred dialog. The size lives only as long as one opening:
 * the dialog never shrinks below the size it opened at and never outgrows the pane.
 */
export const useModalResize = ({ open, minWidth }: { open: boolean; minWidth: number }) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const gestureRef = useRef<ResizeGesture | null>(null);
  const minSizeRef = useRef<ModalSize>({ width: minWidth, height: 0 });
  const [size, setSize] = useState<ModalSize | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [wasOpen, setWasOpen] = useState(open);

  // Resetting on open rather than on close keeps the closing animation from snapping back.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSize(null);
  }

  const isResized = size !== null;
  useEffect(() => {
    if (!isResized) return;
    const fitToPane = () =>
      setSize((current) => current && clampModalSize(current, minSizeRef.current, readPaneSize()));
    globalThis.addEventListener('resize', fitToPane);
    return () => globalThis.removeEventListener('resize', fitToPane);
  }, [isResized]);

  const startResize = (event: ReactPointerEvent<HTMLElement>) => {
    const frame = frameRef.current;
    if (event.button !== 0 || !frame) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const frameRect = frame.getBoundingClientRect();
    const width = clientToCssPixels(frameRect.width);
    const height = clientToCssPixels(frameRect.height);
    if (!size) minSizeRef.current = { width: minWidth, height };
    gestureRef.current = {
      pointerX: event.clientX,
      pointerY: event.clientY,
      startSize: { width, height },
    };
    setIsResizing(true);
  };

  const resize = (event: ReactPointerEvent<HTMLElement>) => {
    const gesture = gestureRef.current;
    if (!gesture) return;
    // A centred dialog grows on both sides, so it grows by twice the drag for the corner to follow the pointer.
    const draggedSize = {
      width: gesture.startSize.width + 2 * clientToCssPixels(event.clientX - gesture.pointerX),
      height: gesture.startSize.height + 2 * clientToCssPixels(event.clientY - gesture.pointerY),
    };
    setSize(clampModalSize(draggedSize, minSizeRef.current, readPaneSize()));
  };

  const endResize = () => {
    gestureRef.current = null;
    setIsResizing(false);
  };

  return {
    frameRef,
    size,
    isResizing,
    resizeHandleProps: {
      onPointerDown: startResize,
      onPointerMove: resize,
      onPointerUp: endResize,
      onPointerCancel: endResize,
      onLostPointerCapture: endResize,
    },
  };
};
